import { t, UUID, Money, DateTimeISO } from '../common.ts';

export const ReportPeriod = t.Union([
  t.Literal('today'),
  t.Literal('yesterday'),
  t.Literal('last7'),
  t.Literal('last30'),
  t.Literal('mtd'),
  t.Literal('ytd'),
  t.Literal('custom'),
]);

export const ReportDateRangeQuery = t.Object(
  {
    period: t.Union(
      [
        t.Literal('today'),
        t.Literal('yesterday'),
        t.Literal('last7'),
        t.Literal('last30'),
        t.Literal('mtd'),
        t.Literal('ytd'),
        t.Literal('custom'),
      ],
      { default: 'today' },
    ),
    from: t.Optional(DateTimeISO),
    to: t.Optional(DateTimeISO),
    groupId: t.Optional(UUID),
    partnerId: t.Optional(UUID),
    gameType: t.Optional(t.String()),
    providerId: t.Optional(UUID),
  },
  { $id: 'ReportDateRangeQuery' },
);

// EVERY money figure the dashboard publishes, for one ISO-4217 denomination.
// Money never sums across currencies — THB satang and JPY yen are
// incommensurable minor units — so currency is a grouping dimension on the
// whole money axis, cash flows included. The cash flows used to sit outside
// this row as scalars on the assumption that a brand has one wallet currency;
// an L6 root reads the WHOLE platform, so that assumption added yen to baht and
// labelled the result THB (#461). Magnitudes are display-bounded, so number at
// the edge is fine (§2.3 boundary); the contract is the grouping, not the JS
// type.
export const DashboardCurrencyRow = t.Object(
  {
    currency: t.String(),
    deposit: Money,
    withdraw: Money,
    netFlow: t.Integer({
      description: 'deposit - withdraw in minor units; negative = money left the house',
    }),
    turnover: Money,
    winloss: t.Integer({
      description: 'payout - stake in minor units; negative = players net lost',
    }),
    // `null` = NO DATA: svc-report has no wallet total to read the bonus from
    // yet, and a zero would read as "no bonus was paid". See
    // `DashboardSummary.bonusOutAvailable`.
    bonusOut: t.Union([Money, t.Null()]),
    betCount: t.Integer(),
    activeMembers: t.Integer(),
  },
  { $id: 'DashboardCurrencyRow' },
);

// The head counts stay OUTSIDE `byCurrency`: people are commensurable across
// brands in a way minor units are not, so a whole-scope "1,200 registrations"
// is a true figure while a whole-scope total of mixed minor units is not a
// figure at all. `activeMembers` also appears per currency, counting the
// players of that denomination. There is deliberately no money scalar here.
export const DashboardSummary = t.Object(
  {
    period: ReportPeriod,
    byCurrency: t.Array(DashboardCurrencyRow),
    // `false` = every `bonusOut` is null because there is no data source, not
    // because nothing was paid. The screen renders a dash, never 0.
    bonusOutAvailable: t.Boolean(),
    registeredMembers: t.Integer(),
    activeMembers: t.Integer(),
    depositingMembers: t.Integer(),
    firstDeposits: t.Integer(),
  },
  { $id: 'DashboardSummary' },
);

// One calendar day of settled cash, for ONE denomination. `currency` is read
// from the brand that stamped each request (tenancy.organizations.currency);
// the row carried none at all until #532, so a scope spanning two brands added
// incommensurable minor units and the CSV export labelled the result 'THB'.
// `netFlow` is signed — withdrawals outrunning deposits is a negative day — so
// it is a plain integer rather than `Money`, which carries `minimum: 0`.
export const FinanceDailyRow = t.Object(
  {
    day: t.String({ pattern: '^\\d{4}-\\d{2}-\\d{2}$' }),
    currency: t.String(),
    deposit: Money,
    withdraw: Money,
    netFlow: t.Integer({
      description: 'deposit - withdraw in minor units; negative = money left the house',
    }),
    depositTxCount: t.Integer(),
    withdrawTxCount: t.Integer(),
  },
  { $id: 'FinanceDailyRow' },
);

// The same settled cash grouped by the payment rail instead of the day, one row
// per (currency, channel). A request with no rail recorded is bucketed under
// 'unknown' rather than dropped.
export const FinanceByChannelRow = t.Object(
  {
    channel: t.String(),
    currency: t.String(),
    deposit: Money,
    withdraw: Money,
    netFlow: t.Integer({
      description: 'deposit - withdraw in minor units; negative = money left the house',
    }),
    depositTxCount: t.Integer(),
    withdrawTxCount: t.Integer(),
  },
  { $id: 'FinanceByChannelRow' },
);

// Daily winloss grouped by (day, providerId, gameType, currency). `day` is the
// bet-time period (date_trunc('hour', settled_at)), NOT the row's write stamp.
// `currency` is a grouping dimension so two currencies never collapse into one
// row of summed minor units; money stays number at the display edge.
export const WinlossDailyRow = t.Object(
  {
    day: t.String({ pattern: '^\\d{4}-\\d{2}-\\d{2}$' }),
    providerId: t.Union([UUID, t.Null()]),
    gameType: t.Union([t.String(), t.Null()]),
    currency: t.String(),
    turnover: Money,
    payout: Money,
    winloss: t.Integer(),
    betCount: t.Integer(),
    playerCount: t.Integer(),
  },
  { $id: 'WinlossDailyRow' },
);

// One request status and the volume sitting at it, within one denomination.
// `settled` says whether the cash has actually moved: a withdraw is debited at
// CREATION and only a rejection or a failed payout puts it back, so `approved`
// and `completed` are both money out.
export const FinanceStatusRow = t.Object(
  {
    side: t.Union([t.Literal('deposit'), t.Literal('withdraw')]),
    status: t.String(),
    settled: t.Boolean(),
    amount: Money,
    txCount: t.Integer(),
  },
  { $id: 'FinanceStatusRow' },
);

// Whole-range cash-flow totals for ONE denomination, plus the status breakdown
// behind them. The currency is read from the brand that stamped each row
// (tenancy.organizations.currency); it was a hard-coded 'THB' label on a single
// scalar block until #461, which for any scope spanning two brands was a sum of
// incommensurable minor units rendered by a 2-decimal formatter.
export const FinanceSummaryCurrencyRow = t.Object(
  {
    currency: t.String(),
    deposit: Money,
    depositTxCount: t.Integer(),
    depositingMembers: t.Integer(),
    withdraw: Money,
    withdrawTxCount: t.Integer(),
    withdrawingMembers: t.Integer(),
    withdrawFee: Money,
    withdrawNet: Money,
    netFlow: t.Integer({
      description: 'deposit - withdraw in minor units; negative = money left the house',
    }),
    byStatus: t.Array(FinanceStatusRow),
  },
  { $id: 'FinanceSummaryCurrencyRow' },
);

// An EMPTY list, not a zeroed row, when nothing moved: there is no denomination
// to name a zero after.
export const FinanceSummary = t.Object(
  { byCurrency: t.Array(FinanceSummaryCurrencyRow) },
  { $id: 'FinanceSummary' },
);

// Settled cash grouped by the HOUSE bank account it moved across, one row per
// (currency, account). `bankAccountId` is null for volume carrying no house
// account — a withdraw records its rail only when an operator confirms the
// transfer — and that unattributed bucket is per currency too, because it
// collects rows from every brand in the scope.
export const FinanceByBankRow = t.Object(
  {
    bankAccountId: t.Union([UUID, t.Null()]),
    currency: t.String(),
    bankCode: t.Union([t.String(), t.Null()]),
    accountNumber: t.Union([t.String(), t.Null()]),
    accountName: t.Union([t.String(), t.Null()]),
    deposit: Money,
    depositTxCount: t.Integer(),
    withdraw: Money,
    withdrawTxCount: t.Integer(),
    netFlow: t.Integer({
      description: 'deposit - withdraw in minor units; negative = money left the account',
    }),
  },
  { $id: 'FinanceByBankRow' },
);

export const FinanceByBank = t.Object(
  { items: t.Array(FinanceByBankRow) },
  { $id: 'FinanceByBank' },
);

// One row per (member, currency) so per-currency gambling totals never sum
// across incommensurable minor units. `currency` is the gambling currency of
// the winloss block; deposit/withdraw/bonus (the brand's single wallet
// currency) are attributed to the member's primary currency row. Money stays
// number at the display edge (§2.3); grouping per-currency is the contract.
export const MemberReportRow = t.Object(
  {
    memberId: UUID,
    username: t.String(),
    currency: t.String(),
    deposit: Money,
    withdraw: Money,
    turnover: Money,
    winloss: t.Integer(),
    // `null` = no data source, never zero — see `DashboardSummary.bonusOutAvailable`.
    bonusOut: t.Union([Money, t.Null()]),
    lastBetAt: t.Union([DateTimeISO, t.Null()]),
  },
  { $id: 'MemberReportRow' },
);

// Only the reports svc-report can actually build a file for, and only the
// format it actually builds.
//
// `bonus_summary`, `cashback_summary` and `partner_revenue` were placeholders:
// each re-served the DASHBOARD summary under its own heading, so an operator
// asking for partner revenue downloaded deposits, turnover and win/loss. And
// `xlsx` was accepted and answered with a CSV named `.csv` (#517). Both are
// refused here now — a 422 naming the field beats a file that looks like an
// answer to a different question. `ExportJobView.format` stays wider: rows
// raised before this still carry `xlsx` in the column.
export const ExportReportRequest = t.Object(
  {
    reportCode: t.Union([
      t.Literal('finance_daily'),
      t.Literal('finance_monthly'),
      t.Literal('winloss_daily'),
      t.Literal('winloss_by_provider'),
      t.Literal('member_summary'),
    ]),
    format: t.Literal('csv', { default: 'csv' }),
    from: DateTimeISO,
    to: DateTimeISO,
    filters: t.Record(t.String(), t.Unknown(), { default: {} }),
  },
  { $id: 'ExportReportRequest' },
);

export const ExportJobView = t.Object(
  {
    id: UUID,
    reportCode: t.String(),
    // Wider than `ExportReportRequest.format` on purpose: `xlsx` cannot be
    // asked for any more, but rows that asked for it before are still read back
    // through here (#517).
    format: t.Union([t.Literal('csv'), t.Literal('xlsx')]),
    status: t.Union([t.Literal('queued'), t.Literal('running'), t.Literal('ready'), t.Literal('failed')]),
    requestedBy: UUID,
    requestedAt: DateTimeISO,
    completedAt: t.Union([DateTimeISO, t.Null()]),
    downloadUrl: t.Union([t.String(), t.Null()]),
    errorMessage: t.Union([t.String(), t.Null()]),
  },
  { $id: 'ExportJobView' },
);
